import { CircleHelp, LogOut, Unlock, User } from "lucide-react";

import { Avatar, AvatarSize } from "@/app/(discover)/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { profileButtonDataTestIds } from "@/lib/data-test-ids/profile.data-test-ids";
import { getInitials } from "@/lib/helpers/initials";

interface ProfileButtonProps {
  fullName: string;
  userEmail: string;
  size: AvatarSize;
  onProfile?: () => void;
  onGetSupport?: () => void;
  onSignOut?: () => void;
  isHijacked?: boolean;
  onReleaseHijack?: () => void;
}

export default function ProfileButton({
  fullName,
  userEmail,
  size,
  onProfile,
  onGetSupport,
  onSignOut,
  isHijacked,
  onReleaseHijack,
}: ProfileButtonProps) {
  const initials = getInitials(fullName, 1);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary"
          data-testid={profileButtonDataTestIds.PROFILE_BUTTON}
        >
          <Avatar
            initials={initials}
            size={size}
            testId={profileButtonDataTestIds.PROFILE_BUTTON_AVATAR}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium leading-none">{fullName}</p>
          <p className="mt-1 text-xs leading-none text-muted-foreground truncate">
            {userEmail}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onProfile}
          className="cursor-pointer"
          data-testid={profileButtonDataTestIds.PROFILE_MENU_PROFILE}
        >
          <User />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onGetSupport}
          className="cursor-pointer"
          data-testid={profileButtonDataTestIds.PROFILE_MENU_SUPPORT}
        >
          <CircleHelp />
          Get Support
        </DropdownMenuItem>
        {isHijacked ? (
          <DropdownMenuItem
            onSelect={onReleaseHijack}
            className="cursor-pointer"
            data-testid={profileButtonDataTestIds.PROFILE_MENU_RELEASE_HIJACK}
          >
            <Unlock />
            Release Hijack
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onSignOut}
          variant="destructive"
          className="cursor-pointer"
          data-testid={profileButtonDataTestIds.PROFILE_MENU_LOGOUT}
        >
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
